import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import fnMedian from 'frontend-utils/utils/numeric/find-median';
import { COMPARE_DIFFERENCE_VISUALISATION_MAPPING } from 'lumen/utils/constants/general-constants';

export type Rate = {
  [key: string]: {
    comparison_rates: {
      difference_absolute: number;

      difference_relative: number;
    }[];
  }[];
};

export type Period = {
  rates: Rate;
};

interface HotelRatesTablesCellsThMedianSignature {
  Args: {
    column: {
      compareType: keyof typeof COMPARE_DIFFERENCE_VISUALISATION_MAPPING;
      hotelinfoId: string;
      isDateColumn: boolean;
      isRateColumn: boolean;
      periods: Period[];
      textAlign?: string;
    };
  };
}

export default class HotelRatesTablesCellsThMedian extends Component<HotelRatesTablesCellsThMedianSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get medianRateRowCompareValue() {
    if (this.args.column.isRateColumn) {
      const columnRateChanges: number[] = [];
      const showDifferenceInPercentage =
        COMPARE_DIFFERENCE_VISUALISATION_MAPPING[
          this.args.column.compareType
        ] === 'percentage';

      this.args.column.periods.forEach((period) => {
        const hotelRatesObj = period.rates[this.args.column.hotelinfoId]?.[0];
        // Check needs to be the same as in TdRateCompare: Math.abs(rateToday - compareRate) >= 1
        if (
          hotelRatesObj?.comparison_rates &&
          hotelRatesObj.comparison_rates[0] &&
          Math.abs(hotelRatesObj.comparison_rates[0].difference_absolute) >= 1
        ) {
          if (hotelRatesObj.comparison_rates[0]) {
            const rateChange = showDifferenceInPercentage
              ? hotelRatesObj.comparison_rates[0].difference_relative * 100
              : hotelRatesObj.comparison_rates[0].difference_absolute;
            columnRateChanges.push(rateChange);
          }
        }
      });

      if (columnRateChanges.length > 0) {
        const medianRateChange = Math.round(fnMedian(columnRateChanges) ?? 0);
        const isPositiveChange = medianRateChange > 0;
        const colorClass = isPositiveChange
          ? 'text-success-default'
          : 'text-critical-default';

        if (showDifferenceInPercentage) {
          return {
            change: isPositiveChange
              ? `+${medianRateChange}%`
              : `${medianRateChange}%`,
            class: colorClass,
          };
        }
        return {
          change: this.hotelAndMarketService.formatRate(
            medianRateChange,
            true,
            true,
            { maximumFractionDigits: 0, minimumFractionDigits: 0 },
            true,
          ),
          class: colorClass,
        };
      }
    }

    return {
      change: this.intl.t('rates.changes.no_difference'),
      class: '',
    };
  }

  <template>
    <div
      class="text-200--medium mr-500 flex
        {{if (eq @column.textAlign 'left') 'justify-start' 'justify-end'}}
        truncate"
    >
      {{#if @column.isRateColumn}}
        <div class={{this.medianRateRowCompareValue.class}}>
          {{this.medianRateRowCompareValue.change}}
        </div>
      {{else if @column.isDateColumn}}
        {{t "rates.table.monthly_median"}}
      {{/if}}
    </div>
  </template>
}
